import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { getOrderDetails } from '../../firebase/firestoreService';
import { FaArrowLeft, FaBox, FaTruck, FaCheckCircle, FaShippingFast, FaBarcode } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 2rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #666;
  text-decoration: none;
  margin-bottom: 1.5rem;
  font-weight: 500;
  
  &:hover {
    color: #C41E3A;
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const OrderCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const OrderHeader = styled.div`
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
`;

const OrderTitle = styled.h2`
  color: #333;
  margin: 0 0 0.5rem;
`;

const OrderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const MetaItem = styled.div`
  span {
    display: block;
    
    &:first-child {
      color: #666;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }
    
    &:last-child {
      color: #333;
      font-weight: 500;
    }
  }
`;

const OrderBody = styled.div`
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const OrderItems = styled.div`
  margin-bottom: 2rem;
`;

const OrderItem = styled.div`
  display: flex;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  margin-right: 1rem;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f5f5;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 576px) {
    margin-bottom: 0.75rem;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ItemPrice = styled.div`
  color: #666;
`;

const ItemQuantity = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const OrderSummary = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  
  &.total {
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid #eee;
    font-weight: 600;
  }
`;

const ShippingInfo = styled.div`
  margin-top: 2rem;
`;

const Address = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  
  p {
    margin: 0.25rem 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'background-color: #e6f4ea; color: #137333;';
      case 'processing':
        return 'background-color: #e8f0fe; color: #1a73e8;';
      case 'shipped':
        return 'background-color: #fef7e0; color: #b06000;';
      case 'pending':
      default:
        return 'background-color: #f1f3f4; color: #5f6368;';
    }
  }}
`;

const StatusIcon = styled.span`
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: center;
`;

const ShippingMethod = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const ShippingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e8f0fe;
  color: #1a73e8;
  margin-right: 1rem;
`;

const ShippingDetails = styled.div`
  flex: 1;
`;

const ShippingName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ShippingDescription = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const ShippingPrice = styled.div`
  font-weight: 500;
`;

const Loading = styled.div`
  text-align: center;
  padding: 3rem;
`;

const Error = styled.div`
  text-align: center;
  padding: 3rem;
  color: #C41E3A;
`;

const TrackingInfo = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: #f0f7ff;
  border-radius: 8px;
  border-left: 4px solid #1a73e8;
`;

const TrackingHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const TrackingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #1a73e8;
  color: white;
  margin-right: 1rem;
`;

const TrackingTitle = styled.h4`
  margin: 0;
  color: #333;
`;

const TrackingDetails = styled.div`
  margin-top: 1rem;
`;

const TrackingRow = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TrackingLabel = styled.div`
  width: 120px;
  color: #666;
  font-weight: 500;
`;

const TrackingValue = styled.div`
  flex: 1;
  color: #333;
`;

const TrackingLink = styled.a`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #1a73e8;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0d62d1;
  }
`;

const TrackingNote = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #cfe2ff;
  color: #666;
  font-style: italic;
`;

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const orderData = await getOrderDetails(id);
        
        if (!orderData || !orderData.data) {
          setError('Order not found');
        } else {
          setOrder({...orderData.data, id: orderData.id});
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchOrderDetails();
    }
  }, [id]);
  
  if (loading) {
    return (
      <Container>
        <div>Loading order details...</div>
      </Container>
    );
  }
  
  if (error || !order) {
    return (
      <Container>
        <BackLink to="/dashboard">
          <FaArrowLeft /> Back to Dashboard
        </BackLink>
        <div>{error || 'Order not found'}</div>
      </Container>
    );
  }
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle />;
      case 'shipped':
        return <FaTruck />;
      case 'processing':
      case 'pending':
      default:
        return <FaBox />;
    }
  };
  
  // Get shipping icon
  const getShippingIcon = (shippingOption) => {
    if (!shippingOption) return <FaTruck />;
    
    if (shippingOption.id === 'express' || shippingOption.name?.includes('Express')) {
      return <FaShippingFast />;
    } else if (shippingOption.id === 'economy' || shippingOption.name?.includes('Economy')) {
      return <FaBox />;
    } else {
      return <FaTruck />;
    }
  };
  
  // Get tracking URL
  const getTrackingUrl = (carrier, trackingNumber) => {
    switch (carrier.toLowerCase()) {
      case 'usps':
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
      case 'ups':
        return `https://www.ups.com/track?tracknum=${trackingNumber}`;
      case 'fedex':
        return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${trackingNumber}`;
      case 'dhl':
        return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
      default:
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    }
  };
  
  // Get carrier name
  const getCarrierName = (carrier) => {
    switch (carrier.toLowerCase()) {
      case 'usps':
        return 'USPS';
      case 'ups':
        return 'UPS';
      case 'fedex':
        return 'FedEx';
      case 'dhl':
        return 'DHL';
      default:
        return carrier.toUpperCase();
    }
  };
  
  // Extract order details
  const { 
    orderDetails, 
    customerInfo, 
    status, 
    createdAt,
    tracking
  } = order;
  
  // Handle different order structures
  const items = orderDetails?.items || order.items || [];
  const subtotal = orderDetails?.subtotal || order.subtotal || 0;
  const shipping = orderDetails?.shipping || order.shipping || 0;
  const tax = orderDetails?.tax || order.tax || 0;
  const total = orderDetails?.total || order.total || 0;
  const shippingOption = orderDetails?.shippingOption || null;
  
  return (
    <Container>
      <BackLink to="/dashboard">
        <FaArrowLeft /> Back to Dashboard
      </BackLink>
      
      <OrderCard>
        <OrderHeader>
          <OrderTitle>Order #{id.substring(0, 8)}</OrderTitle>
          <StatusBadge status={status}>
            <StatusIcon>{getStatusIcon(status)}</StatusIcon>
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Processing'}
          </StatusBadge>
          
          <OrderMeta>
            <MetaItem>
              <span>Order Date</span>
              <span>{formatDate(createdAt)}</span>
            </MetaItem>
            
            <MetaItem>
              <span>Total Amount</span>
              <span>{formatCurrency(total)}</span>
            </MetaItem>
            
            <MetaItem>
              <span>Items</span>
              <span>{items.length}</span>
            </MetaItem>
          </OrderMeta>
        </OrderHeader>
        
        <OrderBody>
          {tracking && tracking.trackingNumber && (
            <TrackingInfo>
              <TrackingHeader>
                <TrackingIcon>
                  <FaBarcode />
                </TrackingIcon>
                <TrackingTitle>Tracking Information</TrackingTitle>
              </TrackingHeader>
              
              <TrackingDetails>
                <TrackingRow>
                  <TrackingLabel>Carrier:</TrackingLabel>
                  <TrackingValue>{getCarrierName(tracking.carrier)}</TrackingValue>
                </TrackingRow>
                
                <TrackingRow>
                  <TrackingLabel>Tracking Number:</TrackingLabel>
                  <TrackingValue>{tracking.trackingNumber}</TrackingValue>
                </TrackingRow>
                
                <TrackingRow>
                  <TrackingLabel>Shipped Date:</TrackingLabel>
                  <TrackingValue>
                    {tracking.addedAt ? formatDate(tracking.addedAt) : 'N/A'}
                  </TrackingValue>
                </TrackingRow>
                
                <TrackingLink 
                  href={getTrackingUrl(tracking.carrier, tracking.trackingNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Track Your Package
                </TrackingLink>
                
                {tracking.notes && (
                  <TrackingNote>
                    Note: {tracking.notes}
                  </TrackingNote>
                )}
              </TrackingDetails>
            </TrackingInfo>
          )}
          
          <OrderItems>
            <SectionTitle>Order Items</SectionTitle>
            
            {items.map((item, index) => (
              <OrderItem key={index}>
                <ItemImage>
                  <img src={item.imageUrl || 'https://via.placeholder.com/80x80'} alt={item.name} />
                </ItemImage>
                
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>{formatCurrency(item.price)}</ItemPrice>
                  <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                </ItemDetails>
              </OrderItem>
            ))}
          </OrderItems>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <ShippingInfo>
              <SectionTitle>Shipping Information</SectionTitle>
              
              <Address>
                <p><strong>{customerInfo?.name}</strong></p>
                <p>{customerInfo?.email}</p>
                <p>{customerInfo?.address}</p>
                <p>{customerInfo?.city}, {customerInfo?.state} {customerInfo?.zipCode}</p>
                <p>{customerInfo?.country}</p>
              </Address>
              
              {shippingOption && (
                <ShippingMethod>
                  <ShippingIcon>
                    {getShippingIcon(shippingOption)}
                  </ShippingIcon>
                  <ShippingDetails>
                    <ShippingName>{shippingOption.name}</ShippingName>
                    <ShippingDescription>{shippingOption.description}</ShippingDescription>
                  </ShippingDetails>
                  <ShippingPrice>{formatCurrency(shippingOption.price)}</ShippingPrice>
                </ShippingMethod>
              )}
            </ShippingInfo>
            
            <div>
              <SectionTitle>Order Summary</SectionTitle>
              
              <OrderSummary>
                <SummaryRow>
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </SummaryRow>
                
                <SummaryRow>
                  <span>Shipping</span>
                  <span>{formatCurrency(shipping)}</span>
                </SummaryRow>
                
                <SummaryRow>
                  <span>Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </SummaryRow>
                
                <SummaryRow className="total">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </SummaryRow>
              </OrderSummary>
            </div>
          </div>
        </OrderBody>
      </OrderCard>
    </Container>
  );
};

export default OrderDetails;
